import React from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import type { WorkflowRunGraphNode } from '../workflow-run.types';
import { StepHeader } from './run-step-view-sections';
import { WorkflowRunReadonlyConversation } from '../workflow-run-readonly-conversation';
import {
  useStepInstanceSelection,
  WorkflowRunStepInstancesList,
} from '../workflow-run-step-instances';
import type { WorkflowRunStepView, WorkflowRunStepViewProps } from './types';

const { WorkflowNodeType, WorkflowRunStepStatus } = PersistedWorkflow;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Resolves the agent conversation id from graph node data or linked ATC run key. */
export const resolveAgentRunChatId = (
  node: WorkflowRunGraphNode & { chatId?: string | null }
): string | null => {
  if (node.chatId !== undefined && node.chatId !== null && node.chatId !== '') {
    return node.chatId;
  }
  if (isRecord(node.data)) {
    const conversationId = node.data['conversationId'];
    if (typeof conversationId === 'string' && conversationId !== '') {
      return conversationId;
    }
  }
  return null;
};

const isActiveAgentStep = (status: string | null): boolean =>
  status === WorkflowRunStepStatus.Queued ||
  status === WorkflowRunStepStatus.Running ||
  status === WorkflowRunStepStatus.Incomplete;

const AgentRunStepView: React.FC<WorkflowRunStepViewProps> = ({
  node,
  payload,
  initialInstanceKey,
}) => {
  const instances = payload?.instances ?? [];
  const { selectedInstanceKey, setSelectedInstanceKey, selectedInstance } =
    useStepInstanceSelection(instances, initialInstanceKey);

  const chatId =
    selectedInstance?.chatId ??
    (instances.length <= 1 ? resolveAgentRunChatId(node) : null);
  const status =
    selectedInstance !== null ? selectedInstance.status : node.status;
  const error = selectedInstance !== null ? selectedInstance.error : node.error;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <div className="shrink-0 space-y-3 border-b border-border/40 px-6 py-4">
        <StepHeader name={node.name} status={status} />
        {error !== null && error !== '' ? (
          <pre className="rounded-lg border border-destructive/30 bg-destructive/5 text-destructive/90 text-xs p-4 overflow-auto whitespace-pre-wrap">
            {error}
          </pre>
        ) : null}
        <WorkflowRunStepInstancesList
          instances={instances}
          selectedInstanceKey={selectedInstanceKey}
          onSelect={setSelectedInstanceKey}
        />
      </div>

      {chatId !== null ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <WorkflowRunReadonlyConversation
            chatId={chatId}
            isActive={isActiveAgentStep(status)}
          />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-8">
          <Text.P className="text-muted-foreground">
            {instances.length > 1
              ? 'Select an instance to view its conversation.'
              : 'No conversation recorded for this step yet.'}
          </Text.P>
        </div>
      )}
    </div>
  );
};

const matchesAgentNodeType = (nodeType: string): boolean =>
  nodeType === WorkflowNodeType.Agent ||
  nodeType === WorkflowNodeType.AdHocAgent;

export const agentRunStepView: WorkflowRunStepView = {
  key: 'agent',
  matches: matchesAgentNodeType,
  needsPayload: true,
  render: (props) => <AgentRunStepView {...props} />,
};
